/* eslint-disable max-lines -- report page orchestrator; already near the cap before AP-764 added the selected-measurement wiring */
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { notification, Alert } from 'antd';

import {
  useFinalizeReportMutation,
  useGetStudyReportsQuery,
  useUpdateReportMutation,
  useCreateaddendumMutation,
  useGetWorksheetQuery,
} from '@/state/slices/reports.ts';
import {
  selectActiveMeasurement,
  selectSelectedAnnotationUID,
  selectAnnotationToMeasurement,
  selectSelectedImageTools,
} from '@/state/slices/measurementSession';
import { Study } from '@/models/study';
import { SettingsContext } from '@/state/contexts';
import { useAuthUser } from '@/hooks/useAuthUser';
import { usePermissions } from '@/hooks/usePermissions';
import { useFlag } from '@/hooks/useFlag';
import { useLogItem } from '@/hooks/useLogitem';
import { ActionTypeChoices, ObjectTypeChoices } from '@/models/logItem';
import {
  AddendumModal,
  ReportFooter,
  resolveMeasurementRows,
} from '@/features/report';
import { CopilotDraft } from '@/features/copilot';
import { useStudyReadiness } from '@/features/study-detail/studyReadiness';
import {
  setReportNavigationGuard,
  clearReportNavigationGuard,
} from '@/features/study-detail/reportNavigationGuard';
import { useWorksheetHistoryApplier } from '@/features/study-detail/history/useWorksheetHistoryApplier';
import { useHistoryHotkeys } from '@/features/study-detail/history/useHistoryHotkeys';
import { useHistoryFeedback } from '@/features/study-detail/history/useHistoryFeedback';
import { useStudyHistoryScope } from '@/features/study-detail/history/useStudyHistoryScope';
import { ageInYears } from '@/shared/utils/patientAge';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import styles from './Report.module.css';

import ReportHistory from './ReportHistory';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useAutoSave } from './hooks/useAutoSave';
import { useReportEditorState } from './hooks/useReportEditorState';
import { useMeasurementHydration } from './hooks/useMeasurementHydration';
import { useMeasurementHandlers } from './hooks/useMeasurementHandlers';
import { useReportFlags } from './hooks/useReportFlags';
import { useAddendumFlow } from './hooks/useAddendumFlow';
import { contextMeasurementGroup } from './derived/contextMeasurements';
import { useReportSavePipeline } from './hooks/useReportSavePipeline';
import { useReportPdfPrefetch } from './hooks/useReportPdfPrefetch';
import { useWorksheetSlots } from './hooks/useWorksheetSlots';
import { useOpenGuideline } from './hooks/useOpenGuideline';
import { buildFooterSignature } from './utils/buildFooterSignature';

interface IProps {
  studyUuid: string;
  study: Study;
  onGoToAnnotation?: (dicomUuid: string, frameNumber: number) => void;
  /** Opens a set of clips in the viewer grid (AP-965). */
  onOpenDicoms?: (dicomUuids: string[]) => void;
}

const Report: React.FC<IProps> = ({
  studyUuid,
  study,
  onGoToAnnotation,
  onOpenDicoms,
}) => {
  const { log } = useLogItem();

  const { user } = useAuthUser();
  const { canEdit, canFinalize } = usePermissions();
  const { settings, setSettings } = useContext(SettingsContext);
  // The `autosave` flag off turns autosave off without touching the user's setting.
  const autosaveFlag = useFlag('autosave');
  const autoSave = settings.autoSave && autosaveFlag;

  const [historyOpen, setHistoryOpen] = useState(false);

  const { data: reportsPagination, isLoading: isReportsLoading } =
    useGetStudyReportsQuery(studyUuid, {
      skip: !studyUuid,
    });
  const reportData = reportsPagination?.results?.[0] ?? undefined;
  const { data: reportWorksheet, isLoading: isWorksheetLoading } =
    useGetWorksheetQuery(settings.defaultWorksheet || '', {
      skip: !settings.defaultWorksheet,
    });

  // Study-readiness 'worksheet' gate (AP-778): ready when the
  // worksheet template AND the study's report have both settled, so the panel
  // renders labels + values in one commit instead of label-then-value pops. A
  // reader with no default worksheet configured settles immediately — the
  // "no worksheet" alert is its final state.
  const { reportGate } = useStudyReadiness();
  const worksheetGateReady =
    !isReportsLoading && (!settings.defaultWorksheet || !isWorksheetLoading);
  useEffect(() => {
    if (!studyUuid) return;
    reportGate(studyUuid, 'worksheet', worksheetGateReady);
  }, [studyUuid, worksheetGateReady, reportGate]);

  const [finalizeReport, { isLoading: loadingFinalizing }] =
    useFinalizeReportMutation();
  const [updateReport, { isLoading: loadingSaving }] =
    useUpdateReportMutation();
  const [createaddendum, { isLoading: creatingAddendum }] =
    useCreateaddendumMutation();
  const [api, contextHolder] = notification.useNotification();

  // Slot-only keys (measurement records, derived maps, finding handlers…)
  // stay on `editor` and reach the panels via the useWorksheetSlots spread.
  const editor = useReportEditorState({ reportData, reportWorksheet });
  const {
    currentReport,
    setCurrentReport,
    serverBaseline,
    setServerBaseline,
    status,
    setStatus,
    conclusion,
    findings,
    conclusionEditorRef,
  } = editor;

  const activeMeasurement = useSelector(selectActiveMeasurement);
  const selectedImageTools = useSelector(selectSelectedImageTools);

  // Background work 'report-pdf-prefetch' (AP-778 — see studyBackgroundWork.ts)
  useReportPdfPrefetch(studyUuid);
  const openGuideline = useOpenGuideline(api);
  // Selected row (AP-764 §3.10): the canonical whose annotation is the
  // currently-selected trace — set by clicking the annotation in the viewer or
  // clicking the label of an annotated row (both dispatch selectAnnotation).
  const selectedAnnotationUID = useSelector(selectSelectedAnnotationUID);
  const annotationToMeasurement = useSelector(selectAnnotationToMeasurement);
  const selectedCanonicalMeasurement = selectedAnnotationUID
    ? (annotationToMeasurement[selectedAnnotationUID] ?? null)
    : null;
  const {
    addendumVisible,
    previousAddendums,
    hasOpenAddendum,
    addendumOpenedByEmail,
    addendumOpenedByName,
    handleAddendumOk,
    handleAddAddendum,
    setAddendumVisible,
  } = useAddendumFlow({
    currentReport,
    studyUuid,
    activeUserEmail: user.email,
    signerName: `${currentReport?.signature?.signerFirstName ?? ''} ${currentReport?.signature?.signerLastName ?? ''}`,
    createaddendum,
    notification: api,
    showAddendumModal: settings.showAddendumModal,
  });

  // The engine's view of the worksheet: the template's groups plus the
  // Context-section measurements (HT, WT, BSA, HR) as one synthetic group, so
  // they resolve, diverge and cascade like any row. The Measurements panel
  // still renders from the template groups alone.
  const { data: canonicals } = useGetWorksheetCanonicalMeasurementsQuery();
  const engineGroups = useMemo(
    () =>
      reportWorksheet && canonicals
        ? [
            ...reportWorksheet.measurementGroups,
            contextMeasurementGroup(canonicals),
          ]
        : reportWorksheet?.measurementGroups,
    [reportWorksheet, canonicals]
  );

  // Single source of per-row measurement state (AP-764): the same resolved
  // map drives the panel's display AND the click handlers' behavior. Inspect
  // it in React DevTools via the panel's `rowModels` prop.
  const rowModels = useMemo(
    () =>
      resolveMeasurementRows({
        measurementGroups: engineGroups ?? [],
        measurements: currentReport?.measurements ?? [],
        aiMeasurements: currentReport?.aiMeasurements ?? [],
        sex: study.patientSex,
        age: ageInYears(study.patientDateOfBirth, study.studyDate),
        selectedImageTools,
      }),
    [
      engineGroups,
      currentReport?.measurements,
      currentReport?.aiMeasurements,
      study.patientSex,
      study.patientDateOfBirth,
      study.studyDate,
      selectedImageTools,
    ]
  );

  const {
    handleMeasurementClick,
    handleMeasurementFocus,
    handleMeasurementValueChange,
    handleConverge,
    handleContextChange,
  } = useMeasurementHandlers({
    studyUuid,
    currentReport,
    setCurrentReport,
    rowModels,
    measurementGroups: engineGroups ?? [],
    patientSex: study.patientSex,
    onGoToAnnotation,
    canEdit,
  });

  useMeasurementHydration({
    reportData,
    currentReport,
    setCurrentReport,
    study,
    // Not `?? []` — a fresh array each render would churn the hydration effects.
    measurementGroups: engineGroups,
    notification: api,
  });

  const {
    selectedFlags,
    setSelectedFlags,
    followUp,
    setFollowUp,
    orderedSections,
    tagsSection,
    tagsCatalog,
    disabledFlags,
    disabledFlagHints,
    followUpGroups,
    buildFlagsPayload,
    handleFlagToggle,
    handleFollowUpChange,
    handleTagsChange,
  } = useReportFlags({
    currentReport,
    setCurrentReport,
    reportWorksheet,
    userRole: user.role,
    serverBaseline,
  });

  // AP-773: the worksheet is the history's write target for reverted state,
  // and the reader page's undo/redo shortcuts mount here — once, gated by
  // the same rule as editing (R8).
  useWorksheetHistoryApplier({
    currentReport,
    setCurrentReport,
    findings,
    setFindings: editor.setFindings,
    conclusion,
    setConclusion: editor.setConclusion,
    flags: {
      selectedFlags,
      setSelectedFlags,
      followUp,
      setFollowUp,
    },
  });
  useStudyHistoryScope(studyUuid);
  const { showResult: showHistoryResult, contextHolder: historyFeedback } =
    useHistoryFeedback();
  useHistoryHotkeys({
    enabled: canEdit && !!currentReport && !currentReport.isFinalized,
    onResult: showHistoryResult,
  });

  const {
    unsavedChanges,
    contentKey,
    refreshEditBaselineFromEditor,
    handleSaveChange,
    handleFinalizeChange,
  } = useReportSavePipeline({
    snapshot: {
      currentReport,
      status,
      conclusion,
      findings,
      selectedFlags,
      followUp,
      serverBaseline,
      hasOpenAddendum,
    },
    setServerBaseline,
    buildFlagsPayload,
    tagsSection,
    updateReport,
    finalizeReport,
    logSave: (uuid) =>
      log({
        actionType: ActionTypeChoices.WORKSHEET_REPORT_SAVED,
        objectType: ObjectTypeChoices.REPORT,
        objectId: uuid,
      }),
    notification: api,
    addendumOpenedByEmail,
    addendumOpenedByName,
    activeUserEmail: user.email,
  });

  const { isAutoSaving, blocker } = useAutoSave({
    enabled: autoSave,
    onSave: handleSaveChange,
    debounceMs: 1500,
    unsavedChanges,
    changeKey: contentKey,
    warnUnsavedChanges: settings.warnUnsavedChanges,
    skip: !currentReport?.uuid || !!currentReport?.isFinalized,
    stayWithinPathPrefix: `/studies/${studyUuid}`,
  });

  // AP-778 §7.1: publish the click-time navigation guard so the worklist
  // gesture can be withheld exactly when the unsaved-changes blocker above
  // would stop the navigation. useLayoutEffect, not useEffect: passive
  // effects flush after paint, leaving a window where a fast type-then-click
  // could read a stale guard and fire the gesture the blocker is about to
  // stop — the layout effect commits the snapshot before the browser can
  // deliver that click.
  const guardSkip = !currentReport?.uuid || !!currentReport?.isFinalized;
  const blocksNavigation =
    unsavedChanges && settings.warnUnsavedChanges && !autoSave && !guardSkip;
  useLayoutEffect(() => {
    setReportNavigationGuard({ studyUuid, blocksNavigation });
  }, [studyUuid, blocksNavigation]);
  useEffect(() => () => clearReportNavigationGuard(studyUuid), [studyUuid]);

  const worksheetSlots = useWorksheetSlots({
    ...editor,
    rowModels,
    reportWorksheet,
    orderedSections,
    tagsSection,
    tagsCatalog,
    study,
    selectedFlags,
    followUp,
    disabledFlags,
    disabledFlagHints,
    followUpGroups,
    activeCanonicalMeasurement: activeMeasurement?.canonicalMeasurement ?? null,
    selectedCanonicalMeasurement,
    handleMeasurementClick,
    handleMeasurementFocus,
    handleMeasurementValueChange,
    handleConverge,
    onOpenGuideline: openGuideline,
    handleContextChange,
    handleFlagToggle,
    handleFollowUpChange,
    handleTagsChange,
    onOpenDicoms,
    canEdit,
  });

  return (
    <>
      {contextHolder}
      {historyFeedback}

      <UnsavedChangesModal
        blocker={blocker}
        onConfirmLeave={() => {
          // Snap baseline so derived `unsavedChanges` flips false on the
          // next render, disarming the blocker before it can re-flash
          // during the proceed window.
          refreshEditBaselineFromEditor();
          blocker.proceed?.();
        }}
        onDontWarnAgain={(dontWarn) =>
          setSettings({ warnUnsavedChanges: !dontWarn })
        }
      />

      <ReportHistory
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        reportsPagination={(reportsPagination as any) ?? null}
      />

      <div className={styles.reportContainer}>
        <CopilotDraft report={currentReport} editorRef={conclusionEditorRef} />
        {!reportWorksheet && !isWorksheetLoading && (
          <Alert
            type="info"
            showIcon
            title="No worksheet configured"
            description="Please select a default worksheet in Settings to see measurements and findings."
            style={{ marginBottom: 16 }}
          />
        )}

        {!isWorksheetLoading &&
          worksheetSlots.map((slot) => (
            <React.Fragment key={slot.type}>{slot.render!()}</React.Fragment>
          ))}

        <ReportFooter
          status={status}
          onStatusChange={setStatus}
          onSave={handleSaveChange}
          onFinalize={handleFinalizeChange}
          previewHref={`/reports/${studyUuid}/preview`}
          isSaving={loadingSaving || isAutoSaving}
          unsavedChanges={unsavedChanges}
          isFinalizing={loadingFinalizing}
          canEdit={canEdit}
          canFinalize={canFinalize}
          isSigned={!!currentReport?.isFinalized}
          signature={buildFooterSignature(currentReport?.signature)}
          onAddAddendum={handleAddAddendum}
          isAddingAddendum={creatingAddendum}
          onViewHistory={() => setHistoryOpen(true)}
        />
      </div>

      <AddendumModal
        report={currentReport}
        open={addendumVisible}
        onCancel={() => setAddendumVisible(false)}
        study={study}
        previousAddendums={previousAddendums}
        loading={creatingAddendum}
        onOk={handleAddendumOk}
      />
    </>
  );
};

export default Report;
